import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineFlag,
    MdOutlineLocationOn,
    MdOutlinePrint
    } from 'react-icons/md';
import { useReactToPrint } from 'react-to-print';
import Header from 'components/Header';
import Button from 'components/Button2';
import TextInput from 'components/TextInput2';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import { canRequestEdits, buildEditRequestUrl } from 'global/editRequest';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import Test from 'pages/Class/Tests';
import { flattenKatexUnderlines } from 'global/katexWrap';

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];
const DISABLE_PRINT = ['ReadingTown', 'College Planning Corp', 'A+ Program', 'MT Learning Center', 'PREPARE Test Prep', 'High Peak Education', 'WellEd Labs'];

function TestBCAPreview() {
    const [testType, setTestType] = useState(null);
    const [testName, setTestName] = useState(null);
    const [testModules, setTestModules] = useState(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [isPrint, setIsPrint] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    // Per-(org, test) clean-print allowlist flag from the preview endpoint.
    const [cleanPrintTest, setCleanPrintTest] = useState(false);

    const { user } = useAuthContext();
    // Clean version applies when the org-wide customization is set OR this specific
    // (org, test) pair is on the per-test clean-print allowlist.
    const cleanVersionEnabled = cleanPrintTest || !!user.customizations.find(customization => customization === 'Test Clean Version Print');
    const printRef = useRef(null);
    const navigate = useNavigate();
    const { testId } = useParams();
    const moduleSelectRef = useOutsideClickHandler(() => setTimeout(() => setModuleSelectOpen(false), 10));
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
        else if (moduleIndex > 0) {
            setQuestionIndex(testModules[moduleIndex - 1].questions.length - 1);
            setModuleIndex(moduleIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < testModules[moduleIndex].questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else if (moduleIndex < testModules.length - 1) {
            setModuleIndex(moduleIndex + 1);
            setQuestionIndex(0);
        }
        else {
            // exit the review
            navigate('/tests');
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let passages = document.getElementById('bcaTestPreview__passages');
        let question = document.getElementById('bcaTestPreview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            if (testModules[moduleIndex].subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        if (!header) {
            return; 
        }
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        }

        const dragElement = (event) => {
            event.preventDefault();

            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }
        }

        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        }
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    }

    const printLog = () => {
        axios.post('tutors/tests/printLog/', {
                'testId': testId,
                'cleanVersion': (!showAnswer && cleanVersionEnabled) ? true : false
            })
            .catch(error => {
                console.log(error);
            });
    }
    
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onAfterPrint: () => {
            setIsPrint(false);
            printLog();
        }
    });

    const onPrint = () =>{
        setIsPrint(true);
    }

    useEffect(()=>{
        if(isPrint) {
            renderKatex();
            handlePrint();
        }
    }, [isPrint]);

    useEffect(() => {
        axios.get(`tests/digital/bca/preview/${testId}/`)
            .then(res => {
                setTestName(res.data.name)
                setTestType(res.data.testType)
                setTestModules(res.data.testModules);
                setCleanPrintTest(res.data.cleanPrintTest);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        if (testModules) {
            // dragMove(document.getElementById('shortcutsDrag'), document.getElementById('shortcutsHeader'));
            const explanationDragEl = document.getElementById('explanationDrag');
            const explanationHeaderEl = document.getElementById('explanationHeader');
            if (explanationDragEl && explanationHeaderEl) {
                dragMove(explanationDragEl, explanationHeaderEl);
            }
        
            const shortcutsDragEl = document.getElementById('shortcutsDrag');
            const shortcutsHeaderEl = document.getElementById('shortcutsHeader');
            if (shortcutsDragEl && shortcutsHeaderEl) {
                dragMove(shortcutsDragEl, shortcutsHeaderEl);
            }
        }
    }, [testModules]);

    useLayoutEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [testModules, moduleIndex, questionIndex]);

    useEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [directionsOpen]);

    if (testModules === null) return <Loader />;

    const {
        name,
        questions,
        subject,
        prompt
    } = testModules[moduleIndex];

    return (
        <>
        <Helmet>
            <title>
                Test Preview - Test Center Admin
            </title>
        </Helmet>
        <Header title={testName} prevRoutes={[{ name: 'All Tests', path: '/tests' }]}/>
        <div className='bcaTestPreview__box' onCopy={(event) => { event.preventDefault() }}>
            {/* screen that will be appear when click button is working */}
            {isPrint &&
                <div className='bcaTestPreview__print' ref={printRef}>
                    {testModules.map((testModule, m) => (<>
                        <h2 className={m > 0
                            ? 'bcaTestPreview__title bcaTestPreview__pageBreakBefore'
                            : 'bcaTestPreview__title'
                        }>
                            {testModule.name}
                        </h2>
                        {testModule.questions.map((question, q) => (
                            <>
                                <div className='bcaTestPreview__test bcaTestPreview__pageBreak'>
                                    {/* left passage */}
                                    {question.passages.length > 0 && <>
                                        <div className='bcaTestPreview__material'>
                                            <div className='bcaTestPreview__materialContent'>
                                                <div id='bcaTestPreview__passages' className='bcaTestPreview__passages'>
                                                    {question.figures.map(figure => (
                                                        <img
                                                            className='bcaTestPreview__figure'
                                                            key={figure.id}
                                                            src={figure.image}
                                                            alt={(q + 1).toString()}
                                                        />
                                                    ))}
                                                    {question.passages.map(({ id, passage }) => (
                                                        <div key={id} className='bcaTestPreview__passage'>
                                                            <p
                                                                id={id}
                                                                className='katex bcaTestPreview__passageText'
                                                            >
                                                                {passage}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className='bcaTestPreview__verticalLine'></div>
                                    </>}
                                    {/* right question & answer */}
                                    <div className='bcaTestPreview__question'>
                                        <div className='bcaTestPreview__questionContent'>
                                            {/* header (question num etc) */}
                                            <div className='bcaTestPreview__questionHeader'>
                                                {/* question num */}
                                                <p className="bcaTestPreview__questionNumber">
                                                    {q + 1}
                                                </p>
                                            </div>

                                            <div id='bcaTestPreview__questionChoices' className='bcaTestPreview__questionChoices'>
                                                {testModule.subject === 'Math' &&
                                                    question.figures.map(figure => (
                                                        <img
                                                            className='bcaTestPreview__figure'
                                                            key={figure.id}
                                                            src={figure.image}
                                                            alt={(q + 1).toString()}
                                                        />
                                                    ))
                                                }
                                                {/* question content */}
                                                <p className='bcaTestPreview__questionQuestion'>
                                                    <span className='katex'>
                                                        {question.question}
                                                    </span>
                                                </p>
                                                {question.choices.length
                                                ?  // multiple choice answer
                                                <>
                                                        {question.choices.map((choice, i) => (
                                                            <div key={choice.id} className='bcaTestPreview__questionChoiceWrapper'>
                                                                <button
                                                                    className={(choice.isCorrect && (showAnswer || !cleanVersionEnabled))
                                                                        ? 'bcaTestPreview__questionChoice--correct'
                                                                        : 'bcaTestPreview__questionChoice'
                                                                    }
                                                                    onClick={() => {}}
                                                                >
                                                                    <div className='bcaTestPreview__questionChoiceOutline'></div>
                                                                    <p className='bcaTestPreview__questionChoiceLetter'>
                                                                        {MULTIPLE_CHOICES[i]}
                                                                    </p>
                                                                    {choice.figure
                                                                    ?   <img
                                                                            className='bcaTestPreview__questionChoiceFigure'
                                                                            key={choice.figure}
                                                                            src={choice.figure}
                                                                            alt={choice.figure}
                                                                        />
                                                                    :   <p className='bcaTestPreview__questionChoiceText'>
                                                                            <span className='katex'>
                                                                                {choice.choice}
                                                                            </span>
                                                                        </p>
                                                                    }
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </>
                                                : // input answer
                                                <div className='bcaTestPreview__questionAnswerReview'>
                                                        <TextInput
                                                            className='bcaTestPreview__questionAnswer'
                                                            inputClassName='bcaTestPreview__questionAnswerInput'
                                                            name={name + q}
                                                            value={(showAnswer || !cleanVersionEnabled) ? question.correctAnswers.join(', ') : ''}
                                                        />
                                                    </div>
                                                }
                                                <div className='bcaTestPreview__marginBottom'></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {(showAnswer || !cleanVersionEnabled) && <>
                                <div>Explanation: </div>
                                {question.explanationFigure && (
                                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                        <img
                                            src={question.explanationFigure}
                                            alt="Explanation Figure"
                                            style={{ maxWidth: question.explanation ? '70%' : '100%', height: 'auto' }}
                                        />
                                    </div>
                                )}
                                {question.explanation &&
                                    <div className='bcaTestPreview__printExpl'>
                                        <span className='katex'>{question.explanation}</span>
                                    </div>
                                }
                                </>}
                            </>
                        ))}
                    </>))}
                </div>
            }


            <div className='bcaTestPreview'>
                {/* test Header */}
                <div className='bcaTestPreview__header'>
                    <div className='bcaTestPreview__titleGroup'>
                        <button className='bcaTestPreview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='bcaTestPreview__title'>
                                {name}
                            </h1>
                            {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='bcaTestPreview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='bcaTestPreview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='bcaTestPreview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((testModule, t) => (
                                        <p
                                            className={t === moduleIndex
                                                ? 'bcaTestPreview__moduleSelectDropdownItem--selected'
                                                : 'bcaTestPreview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {testModule.name}
                                        </p>
                                    ))}
                                </div>
                            }
                        </button>
                        <button className='bcaTestPreview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='bcaTestPreview__directionsIcon' />
                                : <MdKeyboardArrowDown className='bcaTestPreview__directionsIcon' />
                            }
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='bcaTestPreview__directions' ref={directionsRef}>
                            <div className='bcaTestPreview__directionsBubble'>
                                <p className='katex bcaTestPreview__directionsText'>
                                    {prompt
                                        ? prompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='bcaTestPreview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }

                    {/* header action */}
                    <div className='bcaTestPreview__actionGroup'>
                        {canRequestEdits(user) && questions[questionIndex].questionId &&
                            <a
                                className='bcaTestPreview__action bcaTestPreview__editRequest'
                                href={buildEditRequestUrl(questions[questionIndex].questionId, user)}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <MdOutlineFlag className='bcaTestPreview__actionIcon' />
                                <p className='bcaTestPreview__actionText'>Edit Request</p>
                            </a>
                        }
                        {!(user.organizations.some(organization => DISABLE_PRINT.includes(organization))
                            || user.customizations.find(customization => customization === 'Disable Print')
                        ) &&
                            <button
                                className='bcaTestPreview__action'
                                onClick={onPrint}
                            >
                                <MdOutlinePrint className='bcaTestPreview__actionIcon' />
                                <p className='bcaTestPreview__actionText'>Print</p>
                            </button>
                        }
                        <button
                            className='bcaTestPreview__action'
                            onClick={() => navigate(-1)}
                        >
                            <MdExitToApp className='bcaTestPreview__actionIcon' />
                            <p className='bcaTestPreview__actionText'>Exit</p>
                        </button>
                    </div>
                    <div id='explanationDrag' className={showExplanation
                        ? 'bcaTestPreview__popup--explanation'
                        : 'bcaTestPreview__popup--hidden'
                    }>
                        <div id='explanationHeader' className='bcaTestPreview__popupHeader'>
                            <h3 className='bcaTestPreview__popupHeaderText'>Explanation</h3>
                            <MdClose className='bcaTestPreview__popupHeaderIcon' onClick={() => setShowExplanation(false)} />
                        </div>
                        <div className='bcaTestPreview__explanationContainer'>
                            {questions[questionIndex].explanationFigure && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <img
                                        src={questions[questionIndex].explanationFigure}
                                        alt="Explanation Figure"
                                        style={{ maxWidth: questions[questionIndex].explanation ? '80%' : '100%', height: 'auto' }}
                                    /> 
                                </div> 
                            )}
                            <div className='bcaTestPreview__explanationBox katex'>
                                {questions[questionIndex].explanation
                                    ? questions[questionIndex].explanation
                                    : questions[questionIndex].explanationFigure
                                    ? ''
                                    : 'No explanation'
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className='bcaTestPreview__dottedLine'></div>


                {/* test content */}
                <div className='bcaTestPreview__test'>
                    {/* left-passage */}
                    {questions[questionIndex].passages.length > 0 && <>
                        <div className='bcaTestPreview__material'>
                            <div className='bcaTestPreview__materialContent'>
                                <div id='bcaTestPreview__passages' className='bcaTestPreview__passages'>
                                    {questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='bcaTestPreview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                            draggable={false}
                                        />
                                    ))}
                                    {questions[questionIndex].passages.map(({ id, passage }) => (
                                        <div key={id} className='bcaTestPreview__passage'>
                                            <p
                                                id={id}
                                                className='katex bcaTestPreview__passageText'
                                            >
                                                {passage}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className='bcaTestPreview__verticalLine'></div>
                    </>}
                    
                    {/* right-question */}
                    <div className='bcaTestPreview__question'>
                        <div className='bcaTestPreview__questionContent'>
                            {/* header */}
                            <div className='bcaTestPreview__questionHeader'>
                                <p className="bcaTestPreview__questionNumber">
                                    {questionIndex + 1}
                                </p>
                                <div className='bcaTestPreview__questionButtons'>
                                    {(questions[questionIndex].explanation || questions[questionIndex].explanationFigure) &&
                                        <button
                                            className={showExplanation
                                                ? 'bcaTestPreview__explanation--active'
                                                : 'bcaTestPreview__explanation'
                                            }
                                            onClick={() => setShowExplanation(!showExplanation)}
                                        >
                                            Explanation
                                        </button>
                                    }
                                    <button
                                        className={showAnswer
                                            ? 'bcaTestPreview__showAnswer--active'
                                            : 'bcaTestPreview__showAnswer'
                                        }
                                        onClick={() => setShowAnswer(!showAnswer)}
                                    >
                                        {showAnswer ? 'Hide answer' : 'Show answer'}
                                    </button>
                                </div>
                            </div>

                            <div id='bcaTestPreview__questionChoices' className='bcaTestPreview__questionChoices'>
                                {subject === 'Math' &&
                                    questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='bcaTestPreview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                            draggable={false}
                                        />
                                    ))
                                }
                                {/* question content */}
                                <p className='bcaTestPreview__questionQuestion'>
                                    <span className='katex'>
                                        {questions[questionIndex].question}
                                    </span>
                                </p>
                                {questions[questionIndex].choices.length
                                ?   // multiple chioice
                                    <>
                                        {questions[questionIndex].choices.map((choice, i) => (
                                            <div key={choice.id} className='bcaTestPreview__questionChoiceWrapper'>
                                                <button 
                                                    className={showAnswer && choice.isCorrect
                                                        ? 'bcaTestPreview__questionChoice--correct'
                                                        : 'bcaTestPreview__questionChoice'
                                                    }
                                                    onClick={() => {}}
                                                >
                                                    <div className='bcaTestPreview__questionChoiceOutline'></div>
                                                    <p className='bcaTestPreview__questionChoiceLetter'>
                                                        {MULTIPLE_CHOICES[i]}
                                                    </p>
                                                    {choice.figure
                                                    ?   <img
                                                            className='bcaTestPreview__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure}
                                                            alt={choice.figure}
                                                            draggable={false}
                                                        />
                                                    :   <p className='bcaTestPreview__questionChoiceText'>
                                                            <span className='katex'>
                                                                {choice.choice}
                                                            </span>
                                                        </p>
                                                    }
                                                </button>
                                            </div>
                                        ))}
                                    </>
                                : // input answer
                                   <div className='bcaTestPreview__questionAnswerReview'>
                                        <TextInput
                                            className='bcaTestPreview__questionAnswer'
                                            inputClassName='bcaTestPreview__questionAnswerInput'
                                            name={name + questionIndex}
                                            value={''}
                                        />
                                        {showAnswer &&
                                            <p className='bcaTestPreview__questionCorrectAnswer'>
                                                {questions[questionIndex].correctAnswers.toString().replaceAll(',', ', ')}
                                            </p>
                                        }
                                    </div>
                                }
                                <div className='bcaTestPreview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='bcaTestPreview__dottedLine'></div>
                
                {/* text content bottom */}
                <div className='bcaTestPreview__footer'>
                    <button
                        className='bcaTestPreview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='bcaTestPreview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='bcaTestPreview__navigatorToggleIcon' />
                        }
                    </button>

                    <div className='bcaTestPreview__buttons'>
                        <Button
                            className='bcaTestPreview__button'
                            secondary
                            disabled={moduleIndex === 0 && questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='bcaTestPreview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>

                    {/* bottom 'Question' button opened */}
                    {navigatorOpen &&
                        <div className='bcaTestPreview__navigator' ref={navigatorRef}>
                            <div className='bcaTestPreview__navigatorBubble'>
                                <h2 className='bcaTestPreview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='bcaTestPreview__navigatorLegends'>
                                    <div className='bcaTestPreview__navigatorLegend'>
                                        <MdOutlineLocationOn className='bcaTestPreview__navigatorLegendCurrent' />
                                        <p className='bcaTestPreview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                </div>
                                <div className='bcaTestPreview__navigatorItems'>
                                    {questions.map((question, i) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className="bcaTestPreview__navigatorItem--correct"
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='bcaTestPreview__navigatorItemNumber'>{i + 1}</u>
                                                : <p className='bcaTestPreview__navigatorItemNumber'>{i + 1}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='bcaTestPreview__navigatorItemCurrent' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }

                </div>
            </div>
        </div>
        </>
    );
}

export default TestBCAPreview;